import LogtoClient from '@logto/browser';
import type {
  AddonCloudAccount,
  AddonCloudWorkspace,
  AddonRasterAsset,
  AddonRasterAssetUploadInput,
  AddonRasterGeneration,
  AddonRasterGenerationInput,
  AddonRasterLabCapabilities,
  AddonRasterLabJob,
  AddonRasterLabJobInput,
} from '../editor/addons/types';
import {
  inspectRasterHeader,
  MAX_AUTHENTICATED_RASTER_BYTES,
  readValidatedRasterResponse,
} from './image-safety';
import {
  cloudRequestHeaders,
  generationRequestInit,
  rasterAssetCommitRequestInit,
  rasterAssetContentRequestInit,
  rasterAssetIntentRequestInit,
  rasterJobRequestInit,
} from './request';

function envValue(value: unknown, fallback: string): string {
  return typeof value === 'string' && value ? value : fallback;
}

const apiUrl = envValue(import.meta.env.VITE_MAIPO_API_URL, 'https://api.maipo.kua.cl').replace(/\/+$/, '');
const apiResource = envValue(import.meta.env.VITE_LOGTO_API_RESOURCE, 'https://api.maipo.kua.cl');
const logtoEndpoint = envValue(import.meta.env.VITE_LOGTO_ENDPOINT, 'https://auth.kua.cl').replace(/\/+$/, '');
const logtoAppId = envValue(import.meta.env.VITE_LOGTO_APP_ID, '');
const callbackUri = `${window.location.origin}/auth/callback`;

/**
 * Centralized ADR-003 wire paths. The asset upload URL returned by the API is
 * required to equal the corresponding same-origin `assetContent` path.
 */
export const RASTER_LAB_PATHS = {
  capabilities: '/v1/raster/capabilities',
  assetUploadIntents: '/v1/raster/assets/upload-intents',
  assetCommit: (assetId: string) => `/v1/raster/assets/${encodeURIComponent(assetId)}/commit`,
  assetContent: (assetId: string) => `/v1/raster/assets/${encodeURIComponent(assetId)}/content`,
  jobs: '/v1/raster/jobs',
  job: (jobId: string) => `/v1/raster/jobs/${encodeURIComponent(jobId)}`,
} as const;

const logto = logtoAppId
  ? new LogtoClient({
    endpoint: logtoEndpoint,
    appId: logtoAppId,
    resources: [apiResource],
    scopes: ['email'],
  }, true)
  : null;

export type CloudWorkspace = AddonCloudWorkspace;
export type CloudAccount = AddonCloudAccount;

export type CloudGeneration = AddonRasterGeneration;

export interface CloudDocument {
  id: string;
  name: string;
  currentVersionId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CloudAuthState {
  configured: boolean;
  authenticated: boolean;
}

interface ApiErrorBody {
  error?: string;
  message?: string;
  currentVersionId?: string | null;
}

export class CloudApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly currentVersionId?: string | null;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message || body.error || `Maipo Cloud returned HTTP ${String(status)}.`);
    this.status = status;
    if (body.error) this.code = body.error;
    if ('currentVersionId' in body) this.currentVersionId = body.currentVersionId;
  }
}

export async function initializeCloudAuth(): Promise<CloudAuthState> {
  if (!logto) return { configured: false, authenticated: false };
  if (await logto.isSignInRedirected(window.location.href)) {
    await logto.handleSignInCallback(window.location.href);
    window.history.replaceState({}, '', '/');
  }
  return { configured: true, authenticated: await logto.isAuthenticated() };
}

export async function getCloudAuthState(): Promise<CloudAuthState> {
  return {
    configured: Boolean(logto),
    authenticated: logto ? await logto.isAuthenticated() : false,
  };
}

export async function signInToCloud(): Promise<void> {
  if (!logto) throw new Error('Maipo Cloud sign-in is not configured.');
  await logto.signIn({ redirectUri: callbackUri, postRedirectUri: window.location.origin });
}

export async function signOutOfCloud(): Promise<void> {
  if (!logto) return;
  await logto.signOut(window.location.origin);
}

async function apiFetch(
  path: string,
  options: RequestInit = {},
  workspaceId?: string,
): Promise<Response> {
  if (!logto) throw new Error('Maipo Cloud sign-in is not configured.');
  const token = await logto.getAccessToken(apiResource);
  const response = await fetch(`${apiUrl}${path}`, {
    ...options,
    headers: cloudRequestHeaders(token, options, workspaceId),
  });
  if (!response.ok) {
    let body: ApiErrorBody = {};
    try {
      body = await response.json() as ApiErrorBody;
    } catch {
      // Preserve the status when an upstream proxy does not return JSON.
    }
    throw new CloudApiError(response.status, body);
  }
  return response;
}

async function apiRequest<T>(
  path: string,
  options: RequestInit = {},
  workspaceId?: string,
): Promise<T> {
  const response = await apiFetch(path, options, workspaceId);
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export function loadCloudAccount(): Promise<CloudAccount> {
  return apiRequest<CloudAccount>('/v1/me');
}

export async function listCloudDocuments(workspaceId: string): Promise<CloudDocument[]> {
  const result = await apiRequest<{ documents: CloudDocument[] }>('/v1/documents', {}, workspaceId);
  return result.documents;
}

export function createCloudDocument(workspaceId: string, name: string): Promise<CloudDocument> {
  return apiRequest<CloudDocument>('/v1/documents', {
    method: 'POST',
    body: JSON.stringify({ name }),
  }, workspaceId);
}

export function createCloudGeneration(input: AddonRasterGenerationInput): Promise<CloudGeneration> {
  return apiRequest<CloudGeneration>('/v1/generate', generationRequestInit(input), input.workspaceId);
}

export function getCloudGeneration(
  workspaceId: string,
  generationId: string,
  signal?: AbortSignal,
): Promise<CloudGeneration> {
  return apiRequest<CloudGeneration>(
    `/v1/generations/${encodeURIComponent(generationId)}`,
    signal ? { signal } : {},
    workspaceId,
  );
}

export async function downloadCloudGenerationAsset(
  workspaceId: string,
  generationId: string,
  signal?: AbortSignal,
): Promise<Blob> {
  const response = await apiFetch(
    `/v1/generations/${encodeURIComponent(generationId)}/asset`,
    {
      headers: { accept: 'image/*' },
      ...(signal ? { signal } : {}),
    },
    workspaceId,
  );
  return readValidatedRasterResponse(response);
}

interface RasterAssetUploadIntent {
  asset: AddonRasterAsset;
  upload: {
    method: 'PUT';
    url: string;
    headers: Record<string, string>;
    expiresAt: string;
  };
}

function abortError(): DOMException {
  return new DOMException('Aborted', 'AbortError');
}

function safeOpaqueId(value: unknown, label: string): string {
  if (typeof value !== 'string' || value.length < 1 || value.length > 300) {
    throw new Error(`Maipo Cloud returned an invalid Raster Lab ${label}.`);
  }
  return value;
}

function validateRasterAsset(
  asset: AddonRasterAsset,
  input: Pick<AddonRasterAssetUploadInput, 'kind' | 'width' | 'height'>,
): AddonRasterAsset {
  safeOpaqueId(asset?.id, 'asset id');
  if (asset.kind !== input.kind) throw new Error('Maipo Cloud returned the wrong Raster Lab asset kind.');
  if (!['AWAITING_UPLOAD', 'READY', 'QUARANTINED'].includes(asset.state)) {
    throw new Error('Maipo Cloud returned an invalid Raster Lab asset state.');
  }
  if (
    asset.contentType !== null
    && !['image/png', 'image/jpeg', 'image/webp'].includes(asset.contentType)
  ) {
    throw new Error('Maipo Cloud returned a Raster Lab asset with an unsupported content type.');
  }
  if (
    asset.sizeBytes !== null
    && (
      !Number.isSafeInteger(asset.sizeBytes)
      || asset.sizeBytes < 1
      || asset.sizeBytes > MAX_AUTHENTICATED_RASTER_BYTES
    )
  ) {
    throw new Error('Maipo Cloud returned a Raster Lab asset with an unsafe byte size.');
  }
  if (
    (asset.width !== null && asset.width !== input.width)
    || (asset.height !== null && asset.height !== input.height)
  ) {
    throw new Error('Maipo Cloud returned a Raster Lab asset with different dimensions.');
  }
  if (asset.sha256 !== null && !/^[a-f0-9]{64}$/.test(asset.sha256)) {
    throw new Error('Maipo Cloud returned a Raster Lab asset with an invalid digest.');
  }
  return asset;
}

async function rasterBlobDigest(
  input: AddonRasterAssetUploadInput,
): Promise<{ blob: Blob; sha256: string }> {
  if (input.signal?.aborted) throw abortError();
  if (input.blob.size < 1) throw new Error('Raster Lab input is empty.');
  if (input.blob.size > MAX_AUTHENTICATED_RASTER_BYTES) {
    throw new Error('Raster Lab input exceeds the 25 MiB browser safety limit.');
  }
  const bytes = new Uint8Array(await input.blob.arrayBuffer());
  if (input.signal?.aborted) throw abortError();
  const header = inspectRasterHeader(bytes, input.blob.type);
  if (header.width !== input.width || header.height !== input.height) {
    throw new Error('Raster Lab input dimensions do not match its image header.');
  }
  if (input.kind === 'MASK' && header.contentType !== 'image/png') {
    throw new Error('Raster Lab masks must be static PNG images.');
  }
  const digest = new Uint8Array(await globalThis.crypto.subtle.digest('SHA-256', bytes));
  const sha256 = [...digest].map((value) => value.toString(16).padStart(2, '0')).join('');
  return {
    blob: input.blob.type === header.contentType
      ? input.blob
      : new Blob([bytes], { type: header.contentType }),
    sha256,
  };
}

export function loadRasterLabCapabilities(
  workspaceId: string,
  signal?: AbortSignal,
): Promise<AddonRasterLabCapabilities> {
  return apiRequest<AddonRasterLabCapabilities>(
    RASTER_LAB_PATHS.capabilities,
    signal ? { signal } : {},
    workspaceId,
  );
}

export async function uploadRasterLabAsset(
  input: AddonRasterAssetUploadInput,
): Promise<AddonRasterAsset> {
  const prepared = await rasterBlobDigest(input);
  const canonicalInput: AddonRasterAssetUploadInput = {
    ...input,
    blob: prepared.blob,
  };
  const intent = await apiRequest<RasterAssetUploadIntent>(
    RASTER_LAB_PATHS.assetUploadIntents,
    rasterAssetIntentRequestInit(canonicalInput, prepared.sha256),
    input.workspaceId,
  );
  const asset = validateRasterAsset(
    intent.asset,
    input,
  );
  if (asset.state === 'QUARANTINED') throw new Error('Raster Lab rejected this input asset.');
  if (asset.state === 'READY') return asset;
  const expectedContentPath = RASTER_LAB_PATHS.assetContent(asset.id);
  if (intent.upload?.method !== 'PUT' || intent.upload.url !== expectedContentPath) {
    throw new Error('Maipo Cloud returned an invalid Raster Lab upload target.');
  }
  await apiFetch(
    expectedContentPath,
    rasterAssetContentRequestInit(prepared.blob, input.signal),
    input.workspaceId,
  );
  const committed = await apiRequest<AddonRasterAsset>(
    RASTER_LAB_PATHS.assetCommit(asset.id),
    rasterAssetCommitRequestInit(prepared.sha256, input.signal),
    input.workspaceId,
  );
  validateRasterAsset(
    committed,
    input,
  );
  if (committed.state !== 'READY') throw new Error('Raster Lab asset did not become ready after upload.');
  return committed;
}

export function createRasterLabJob(input: AddonRasterLabJobInput): Promise<AddonRasterLabJob> {
  return apiRequest<AddonRasterLabJob>(
    RASTER_LAB_PATHS.jobs,
    rasterJobRequestInit(input),
    input.workspaceId,
  );
}

export function getRasterLabJob(
  workspaceId: string,
  jobId: string,
  signal?: AbortSignal,
): Promise<AddonRasterLabJob> {
  return apiRequest<AddonRasterLabJob>(
    RASTER_LAB_PATHS.job(safeOpaqueId(jobId, 'job id')),
    signal ? { signal } : {},
    workspaceId,
  );
}

export async function downloadRasterLabAsset(
  workspaceId: string,
  assetId: string,
  signal?: AbortSignal,
): Promise<Blob> {
  const response = await apiFetch(
    RASTER_LAB_PATHS.assetContent(safeOpaqueId(assetId, 'asset id')),
    {
      headers: { accept: 'image/png,image/jpeg,image/webp' },
      ...(signal ? { signal } : {}),
    },
    workspaceId,
  );
  return readValidatedRasterResponse(response);
}

export interface CloudUpload {
  versionId: string;
  uploadUrl: string;
  headers: Record<string, string>;
}

export function beginCloudUpload(workspaceId: string, documentId: string, sizeBytes: number): Promise<CloudUpload> {
  return apiRequest<CloudUpload>(`/v1/documents/${encodeURIComponent(documentId)}/versions/upload-url`, {
    method: 'POST',
    body: JSON.stringify({ sizeBytes }),
  }, workspaceId);
}

export async function uploadCloudBytes(upload: CloudUpload, bytes: Uint8Array): Promise<void> {
  const headers = { ...upload.headers };
  // Content-Length is a forbidden browser header; fetch supplies it from the body.
  delete headers['Content-Length'];
  delete headers['content-length'];
  const response = await fetch(upload.uploadUrl, { method: 'PUT', headers, body: bytes });
  if (!response.ok) throw new Error(`Cloud upload failed with HTTP ${String(response.status)}.`);
}

export function commitCloudVersion(
  workspaceId: string,
  documentId: string,
  versionId: string,
  baseVersionId: string | null,
): Promise<{ versionId: string; currentVersionId: string }> {
  return apiRequest(`/v1/documents/${encodeURIComponent(documentId)}/versions/${encodeURIComponent(versionId)}/commit`, {
    method: 'POST',
    body: JSON.stringify({ baseVersionId }),
  }, workspaceId);
}

export async function downloadCloudDocument(
  workspaceId: string,
  documentId: string,
): Promise<{ bytes: Uint8Array; document: CloudDocument; versionId: string }> {
  const result = await apiRequest<{
    document: CloudDocument;
    versionId: string;
    downloadUrl: string;
  }>(`/v1/documents/${encodeURIComponent(documentId)}/download-url`, {}, workspaceId);
  const response = await fetch(result.downloadUrl);
  if (!response.ok) throw new Error(`Cloud download failed with HTTP ${String(response.status)}.`);
  return {
    bytes: new Uint8Array(await response.arrayBuffer()),
    document: result.document,
    versionId: result.versionId,
  };
}
